import { createContext, RefCallback, useCallback, useContext, useImperativeHandle, useMemo, useRef } from 'react';
import { PanelDefinition } from '@/core';

export interface PanelApi {
  /**
   *  Returns the panel definition
   */
  getPanelDefinition?: () => PanelDefinition | undefined;
}

export function useVizPanelGroupInternal() {
  // Using a Map to store the panel api for each panel key because:
  // - we want to preserve the insertion order, so that panel order remains the same when adding a group to the dashboard
  // - it allows fast lookups and deletions of panel api.
  const panelKeyToPanelApiMap = useRef<Map<string, PanelApi | undefined>>(new Map());

  // Returns all panel definitions that are registered in the group
  const getPanelDefinitions = useCallback(() => {
    const panelDefinitions: PanelDefinition[] = [];
    for (const panelApi of panelKeyToPanelApiMap.current.values()) {
      if (!panelApi) {
        continue;
      }
      const panelDefinition = panelApi.getPanelDefinition?.();
      if (panelDefinition) {
        panelDefinitions.push(panelDefinition);
      }
    }
    return panelDefinitions;
  }, []);

  const ctx = useMemo(() => {
    return {
      panelKeyToPanelApiMap,
      getPanelDefinitions,
    };
  }, [panelKeyToPanelApiMap, getPanelDefinitions]);

  return ctx;
}

export const VizPanelGroupContext = createContext<ReturnType<typeof useVizPanelGroupInternal> | undefined>(undefined);

export function VizPanelGroupProvider({ children }: { children: React.ReactNode }) {
  const ctx = useVizPanelGroupInternal();
  return <VizPanelGroupContext.Provider value={ctx}>{children}</VizPanelGroupContext.Provider>;
}

export function useVizPanelGroupApi() {
  const ctx = useContext(VizPanelGroupContext);
  return ctx;
}

/**
 * Returns a callback ref for use with `useImperativeHandle` in `useRegisterPanelToGroup`.
 * The callback registers the panel api if it exists with the nearest VizPanelGroupProvider.
 * https://react.dev/reference/react-dom/components/common#ref-callback
 */
export function usePanelRef(panelKey: string) {
  const panelKeyToPanelApiMap = useVizPanelGroupApi()?.panelKeyToPanelApiMap;

  // Creates a callback ref that React will invoke when using useImperativeHandle
  const callbackRef: RefCallback<PanelApi | null | undefined> = useMemo(() => {
    return (panelApi?: PanelApi | null) => {
      // Noop if no VizPanelGroupProvider is found
      if (!panelKeyToPanelApiMap) {
        return;
      }

      // panelApi is null when the component unmounts or needs cleanup
      // This means that we should unregister the panel from the group
      if (panelApi === null) {
        panelKeyToPanelApiMap?.current.delete(panelKey);
        return;
      }

      // panelApi is undefined when the registered panel does not have a panel definition
      if (panelApi === undefined) {
        return;
      }

      // panelApi should be the PanelApi object here
      // Store the PanelApi object in the map so VizPanelGroupProvider can access the panel definition when needed
      panelKeyToPanelApiMap?.current.set(panelKey, panelApi);
    };
  }, [panelKey, panelKeyToPanelApiMap]);

  return callbackRef;
}

/**
 * Hook for panels to register their panel definition with the nearest VizPanelGroupProvider.
 */
export function useRegisterPanelToGroup(panelKey: string, panelDefinition: PanelDefinition | undefined) {
  const panelApiCallbackRef = usePanelRef(panelKey);

  // useImperativeHandle takes the panelApiCallbackRef and:
  // - calls it with the PanelApi object when component mounts/updates and there is a panel definition
  // - calls it with undefined when there is no panel definition
  // - calls it with null when component unmounts or dependencies change
  useImperativeHandle(panelApiCallbackRef, () => {
    if (!panelDefinition) {
      return undefined;
    }

    return {
      getPanelDefinition: () => panelDefinition,
    };
  }, [panelDefinition]);
}
